import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import {
  ACCESS_TIERS,
  CONTENT_OWNER_TYPES,
  CONTENT_VISIBILITY,
  PLATFORM_ROLES,
} from './platformSchema';
import { canManageOrganization, canEditContentItem } from './platformAccess';
import { resolvePlatformRole } from './organizationContext';

function sanitizeString(value) {
  return String(value || '').trim();
}

function sanitizeArray(values = []) {
  return (Array.isArray(values) ? values : [values])
    .map((value) => sanitizeString(value))
    .filter(Boolean);
}

function normalizeVisibilityForWrite(value = '') {
  if (value === CONTENT_VISIBILITY.ORGANIZATION || value === 'org' || value === 'school') {
    return CONTENT_VISIBILITY.ORGANIZATION;
  }
  if (value === CONTENT_VISIBILITY.PLATFORM) {
    return CONTENT_VISIBILITY.PLATFORM;
  }
  if (value === CONTENT_VISIBILITY.ASSIGNED_ONLY) {
    return CONTENT_VISIBILITY.ASSIGNED_ONLY;
  }
  return CONTENT_VISIBILITY.PRIVATE;
}

export function normalizeDrillTemplateForWrite(
  template = {},
  {
    currentUser = null,
    userData = {},
    organizationId = '',
  } = {}
) {
  const visibility = normalizeVisibilityForWrite(template.visibility);
  const platformRole = resolvePlatformRole(userData);
  const requestedOwnerType = template.ownerType || CONTENT_OWNER_TYPES.INSTRUCTOR;
  let ownerType = requestedOwnerType;

  if (requestedOwnerType === CONTENT_OWNER_TYPES.PLATFORM) {
    ownerType =
      platformRole === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN ||
      platformRole === PLATFORM_ROLES.PLATFORM_CONTENT_ADMIN
        ? CONTENT_OWNER_TYPES.PLATFORM
        : CONTENT_OWNER_TYPES.INSTRUCTOR;
  } else if (requestedOwnerType === CONTENT_OWNER_TYPES.ORGANIZATION) {
    ownerType = canManageOrganization(userData, organizationId)
      ? CONTENT_OWNER_TYPES.ORGANIZATION
      : CONTENT_OWNER_TYPES.INSTRUCTOR;
  }

  const resolvedOrganizationId =
    ownerType === CONTENT_OWNER_TYPES.ORGANIZATION
      ? sanitizeString(template.organizationId || template.orgId || organizationId)
      : sanitizeString(template.organizationId || template.orgId || organizationId);

  const ownerId =
    ownerType === CONTENT_OWNER_TYPES.PLATFORM
      ? sanitizeString(template.ownerId || 'platform')
      : ownerType === CONTENT_OWNER_TYPES.ORGANIZATION
        ? sanitizeString(template.ownerId || resolvedOrganizationId)
        : sanitizeString(template.ownerId || template.createdBy || currentUser?.uid);

  const accessTier = template.contentPackId
    ? ACCESS_TIERS.PREMIUM
    : template.accessTier || ACCESS_TIERS.FREE;

  return {
    title: sanitizeString(template.title) || 'Untitled Drill',
    summary: sanitizeString(template.summary || template.description),
    description: sanitizeString(template.description),
    sport: sanitizeString(template.sport),
    discipline: sanitizeString(template.discipline),
    level: sanitizeArray(template.level),
    topic: sanitizeString(template.topic),
    commonError: sanitizeString(template.commonError),
    correctionCue: sanitizeString(template.correctionCue),
    focusAreas: sanitizeArray(template.focusAreas),
    tags: sanitizeArray(template.tags),
    steps: sanitizeArray(template.steps),
    relatedSkillIds: Array.isArray(template.relatedSkillIds) ? template.relatedSkillIds.filter(Boolean) : [],
    ownerType,
    ownerId,
    visibility,
    accessTier,
    contentPackId: sanitizeString(template.contentPackId),
    assignedUserIds: sanitizeArray(template.assignedUserIds),
    createdBy: sanitizeString(template.createdBy || currentUser?.uid),
    orgId: resolvedOrganizationId,
    organizationId: resolvedOrganizationId,
    sourceType: sanitizeString(template.sourceType || 'instructor_custom'),
    sourceLabel: sanitizeString(template.sourceLabel),
  };
}

export async function createDrillTemplate(
  db,
  template = {},
  context = {}
) {
  const payload = normalizeDrillTemplateForWrite(template, context);
  const docRef = await addDoc(collection(db, 'drillTemplates'), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    id: docRef.id,
    ...payload,
  };
}

export async function updateDrillTemplate(
  db,
  templateId,
  updates = {},
  context = {}
) {
  if (!templateId) {
    throw new Error('templateId is required');
  }

  const editable = canEditContentItem(
    {
      ...updates,
      ownerType: updates.ownerType,
      ownerId: updates.ownerId || updates.organizationId || updates.orgId || updates.createdBy,
    },
    {
      currentUserId: context.currentUser?.uid || '',
      currentOrganizationIds: context.organizationId ? [context.organizationId] : [],
      userData: context.userData || {},
    }
  );

  if (!editable && resolvePlatformRole(context.userData || {}) !== PLATFORM_ROLES.PLATFORM_SUPER_ADMIN) {
    throw new Error('Not allowed to update this drill template');
  }

  const payload = normalizeDrillTemplateForWrite(updates, context);
  await updateDoc(doc(db, 'drillTemplates', templateId), {
    ...payload,
    updatedAt: serverTimestamp(),
  });

  return {
    id: templateId,
    ...payload,
  };
}
